import { Box, Image, Theme, Typography } from "@outschool/backpack";
import { measurePerformance } from "@outschool/ui-analytics";
import { TrackedButton } from "@outschool/ui-components-shared";
import { TrackingPageSection } from "@outschool/ui-legacy-component-library";
import React from "react";

import Link from "./Link";

export type Topic = {
  image: string;
  text: string;
  path: string;
};

export type TopicsShowcaseProps = {
  title: string;
  topics: Topic[];
  sectionTrackingName: string;
  buttonTrackingName: string;
  imageTrackingName: string;
  titleVariant?: string;
};

function TopicItem({
  topic,
  buttonTrackingName,
  imageTrackingName
}: {
  topic: Topic;
  buttonTrackingName: string;
  imageTrackingName: string;
}) {
  return (
    <TrackedButton
      variant="link"
      size="inherit"
      trackingName={buttonTrackingName}
      trackingUniqueId={buttonTrackingName}
      trackingSharedProperties={{ topic: topic.text.toLowerCase() }}
      component={Link}
      to={topic.path}
      sx={(theme: Theme) => ({
        padding: 0,
        display: "grid",
        alignItems: "center",
        justifyItems: "center",
        [theme.breakpoints.up("md")]: {
          display: "flex",
          justifyItems: "start"
        },
        border: "none !important",
        background: "#f8f8f9 !important"
      })}
    >
      <Box
        sx={(theme: $TSFixMe) => ({
          display: "block",
          width: "88px",
          height: "88px",
          background: "white",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)",
          borderRadius: "100%",
          marginRight: "12px",

          [theme.breakpoints.up("md")]: {
            marginRight: "12px",
            marginBottom: "0"
          },

          marginBottom: "12px"
        })}
      >
        <Image
          onLoad={() => {
            /*
             * This component appears on the logged-in home page and the first
             * image load here is used to measure page load performance.
             * `measurePerformance` becomes a noop after being called once.
             */
            measurePerformance("os-page-load", {
              source: { imageTrackingName }
            });
          }}
          src={topic.image}
          sx={{
            height: "100%",
            width: "100%",
            padding: "10px"
          }}
        />
      </Box>
      <Typography
        variant="h5"
        sx={{
          color: "grey.900",
          textAlign: "left"
        }}
      >
        {topic.text}
      </Typography>
    </TrackedButton>
  );
}

export function TopicsShowcase({
  title,
  topics,
  sectionTrackingName,
  buttonTrackingName,
  imageTrackingName,
  titleVariant = "H3"
}: TopicsShowcaseProps) {
  return (
    <TrackingPageSection
      name={sectionTrackingName}
      uniqueId={sectionTrackingName}
    >
      {ref => (
        <Box
          flex
          ref={ref}
          sx={{
            backgroundColor: "#f8f8f9",
            alignItems: "center",
            flexDirection: "column",
            paddingY: "2em"
          }}
        >
          <Typography align="center" variant={titleVariant}>
            {title}
          </Typography>
          <Box
            sx={(theme: $TSFixMe) => ({
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",

              [theme.breakpoints.up("md")]: {
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                justifyItems: "start"
              },

              gap: "30px 15px",
              justifyItems: "center",
              width: "calc(100% - 64px)",
              maxWidth: "1024px",
              paddingY: "30px"
            })}
          >
            {topics.map((topic, index) => (
              <TopicItem
                key={index}
                topic={topic}
                buttonTrackingName={buttonTrackingName}
                imageTrackingName={imageTrackingName}
              />
            ))}
          </Box>
        </Box>
      )}
    </TrackingPageSection>
  );
}
